import {FC, useState, useEffect, useCallback, useMemo} from 'react'
import {
  getPostConfirmations,
  getAccountConfirmations,
  SummaryResponse,
  AccountDetailResponse,
  AccountRow,
  AlertCode,
} from './_requests'

const ALERT_LABELS: Record<AlertCode, string> = {
  not_posting: 'לא מפרסם',
  unconfirmed: 'הבוט מדווח — פייסבוק לא מאשר',
  low_ratio: 'מעט מאוד אישורים',
  declines: 'הרבה דחיות',
  notif_stale: 'ממתין לסנכרון התראות',
  ok: 'תקין',
}

const ALERT_HINTS: Record<AlertCode, string> = {
  not_posting: 'יש סלוטים שעבר זמנם אבל הבוט לא פרסם אף אחד',
  unconfirmed: 'הבוט סימן פרסומים (והפרופילים סנכרנו התראות אחריהם), אבל אין אף התראת "אושר"/"שותף" מפייסבוק — כנראה הפוסטים לא באמת עולים',
  low_ratio: 'פחות מ-5% מהפרסומים קיבלו אישור (הממוצע בצי ~22%)',
  declines: 'מנהלי קבוצות דוחים יותר פוסטים ממה שמאשרים',
  notif_stale: 'רוב הפרסומים נעשו אחרי הסנכרון האחרון של ההתראות — עוד אי אפשר לשפוט',
  ok: '',
}

const LEVEL_BADGE: Record<string, string> = {
  red: 'badge-light-danger',
  orange: 'badge-light-warning',
  gray: 'badge-light',
  ok: 'badge-light-success',
}

const KIND_LABELS: Record<string, {label: string; badge: string}> = {
  crosspost: {label: 'שותף בקבוצה', badge: 'badge-light-primary'},
  approved: {label: 'אושר ע״י מנהל', badge: 'badge-light-success'},
  scheduled: {label: 'מתוזמן פורסם', badge: 'badge-light-info'},
  declined: {label: 'נדחה', badge: 'badge-light-danger'},
}

const fmtDate = (s?: string | null) => {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('he-IL', {dateStyle: 'short', timeStyle: 'short'})
  } catch {
    return s
  }
}

const ago = (s?: string | null) => {
  if (!s) return 'אף פעם'
  const h = (Date.now() - new Date(s).getTime()) / 3600e3
  if (h < 1) return `לפני ${Math.max(1, Math.round(h * 60))} דק׳`
  if (h < 48) return `לפני ${Math.round(h)} שע׳`
  return `לפני ${Math.round(h / 24)} ימים`
}

const pct = (live: number, posted: number) => (posted ? `${Math.round((live / posted) * 100)}%` : '—')

const groupLink = (id: string) => `https://www.facebook.com/groups/${id}/`

const StatCard: FC<{label: string; value: string | number; className?: string}> = ({label, value, className}) => (
  <div className='col-6 col-md-3'>
    <div className='card card-flush h-100'>
      <div className='card-body py-4'>
        <div className='text-gray-500 fs-7'>{label}</div>
        <div className={`fs-2 fw-bolder ${className || ''}`}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
      </div>
    </div>
  </div>
)

const AccountDetail: FC<{id: string; days: number; onBack: () => void}> = ({id, days, onBack}) => {
  const [data, setData] = useState<AccountDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [detailDays, setDetailDays] = useState(Math.max(days, 7))

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getAccountConfirmations(id, detailDays)
      .then((res) => { if (!cancelled) setData(res.data) })
      .catch((e) => console.error('Failed to load account confirmations:', e))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, detailDays])

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-6'>
        <div className='d-flex align-items-center gap-3'>
          <button className='btn btn-sm btn-light' onClick={onBack}>
            <i className='bi bi-arrow-right'></i> חזרה
          </button>
          <h2 className='fw-bolder m-0'>{data?.account.name || '...'}</h2>
          {data?.account.phone && <span className='text-gray-500 fs-7' dir='ltr'>{data.account.phone}</span>}
        </div>
        <select className='form-select form-select-sm w-auto' value={detailDays} onChange={(e) => setDetailDays(Number(e.target.value))}>
          <option value={3}>3 ימים</option>
          <option value={7}>7 ימים</option>
          <option value={14}>14 ימים</option>
        </select>
      </div>

      {loading || !data ? (
        <div className='text-center py-10'><span className='spinner-border text-primary' role='status'></span></div>
      ) : (
        <>
          <div className='card card-flush mb-6'>
            <div className='card-header'><h3 className='card-title fw-bold'>לפי יום</h3></div>
            <div className='card-body py-4'>
              <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-2 fs-7'>
                <thead>
                  <tr className='fw-bold text-gray-600'>
                    <th>תאריך</th><th>מתוזמנים</th><th>הבוט פרסם</th><th>אושרו בפייסבוק</th><th>יחס</th><th>נדחו</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((d) => (
                    <tr key={d.date}>
                      <td dir='ltr' style={{textAlign: 'right'}}>{d.date}</td>
                      <td>{d.scheduled}</td>
                      <td>{d.posted}</td>
                      <td className={d.posted >= 20 && d.live === 0 ? 'text-danger fw-bold' : 'fw-bold'}>{d.live}</td>
                      <td>{pct(d.live, d.posted)}</td>
                      <td className={d.declined ? 'text-danger' : 'text-gray-400'}>{d.declined}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className='card card-flush mb-6'>
            <div className='card-header'><h3 className='card-title fw-bold'>פרופילים</h3></div>
            <div className='card-body py-4'>
              <div className='table-responsive'>
                <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-2 fs-7'>
                  <thead>
                    <tr className='fw-bold text-gray-600'>
                      <th>פרופיל</th><th>הגיע זמנם</th><th>הבוט פרסם</th><th>אושרו</th><th>יחס</th><th>נדחו</th><th>אישור אחרון</th><th>סנכרון התראות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.profiles.map((p) => (
                      <tr key={p._id}>
                        <td>{p.name} {p.is_sub && <span className='badge badge-light fs-9'>דף</span>}</td>
                        <td>{p.due}</td>
                        <td className={p.due >= 10 && p.posted === 0 ? 'text-danger fw-bold' : ''}>{p.posted}</td>
                        <td className={p.posted >= 20 && p.live === 0 ? 'text-danger fw-bold' : 'fw-bold'}>{p.live}</td>
                        <td>{pct(p.live, p.posted)}</td>
                        <td className={p.declined ? 'text-danger' : 'text-gray-400'}>{p.declined}</td>
                        <td>{fmtDate(p.last_live_at)}</td>
                        <td className='text-gray-500'>{ago(p.last_notif_sync)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className='card card-flush mb-6'>
            <div className='card-header'><h3 className='card-title fw-bold'>קבוצות ({data.groups.length})</h3></div>
            <div className='card-body py-4'>
              <div className='table-responsive' style={{maxHeight: 520, overflowY: 'auto'}}>
                <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-2 fs-7'>
                  <thead>
                    <tr className='fw-bold text-gray-600'>
                      <th>קבוצה</th><th>הבוט פרסם</th><th>אושרו</th><th>נדחו</th><th>אישור אחרון</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.groups.map((g) => (
                      <tr key={g.group_fb_id}>
                        <td><a href={groupLink(g.group_fb_id)} target='_blank' rel='noreferrer'>{g.name || g.group_fb_id}</a></td>
                        <td>{g.posted}</td>
                        <td className='fw-bold'>{g.live}</td>
                        <td className={g.declined ? 'text-danger' : 'text-gray-400'}>{g.declined}</td>
                        <td>{fmtDate(g.last_live_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className='card card-flush'>
            <div className='card-header'><h3 className='card-title fw-bold'>התראות אחרונות מפייסבוק</h3></div>
            <div className='card-body py-4'>
              {data.recent.length === 0 ? (
                <div className='text-center text-gray-500 py-6'>אין התראות פרסום בתקופה הזו</div>
              ) : (
                <div className='table-responsive' style={{maxHeight: 520, overflowY: 'auto'}}>
                  <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-2 fs-7'>
                    <thead>
                      <tr className='fw-bold text-gray-600'><th>מתי</th><th>סוג</th><th>קבוצה</th><th>פרופיל</th><th></th></tr>
                    </thead>
                    <tbody>
                      {data.recent.map((e) => (
                        <tr key={e._id}>
                          <td className='text-nowrap'>{fmtDate(e.event_at)}</td>
                          <td><span className={`badge ${KIND_LABELS[e.kind]?.badge || 'badge-light'}`}>{KIND_LABELS[e.kind]?.label || e.kind}</span></td>
                          <td>{e.group_name || e.group_fb_id}</td>
                          <td className='text-gray-600'>{e.profile}</td>
                          <td>{e.url && <a href={e.url} target='_blank' rel='noreferrer'>פוסט <i className='bi bi-box-arrow-up-left'></i></a>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const PostConfirmationsPage: FC = () => {
  const [data, setData] = useState<SummaryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(2)
  const [tab, setTab] = useState<'accounts' | 'groups'>('accounts')
  const [alertsOnly, setAlertsOnly] = useState(true)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getPostConfirmations(days)
      setData(res.data)
    } catch (e) {
      console.error('Failed to load post confirmations:', e)
    }
    setLoading(false)
  }, [days])

  useEffect(() => { load() }, [load])

  const accounts = useMemo(() => {
    if (!data) return [] as AccountRow[]
    const q = search.trim().toLowerCase()
    return data.accounts.filter((a) => {
      if (alertsOnly && a.alert === 'ok') return false
      if (q && !(a.name.toLowerCase().includes(q) || a.phone.includes(q) || a.account_id.includes(q))) return false
      return true
    })
  }, [data, alertsOnly, search])

  const groups = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    return q ? data.groups.filter((g) => (g.name || '').toLowerCase().includes(q) || g.group_fb_id.includes(q)) : data.groups
  }, [data, search])

  if (selected) {
    return (
      <div className='container-fluid py-6' dir='rtl'>
        <AccountDetail id={selected} days={days} onBack={() => setSelected(null)} />
      </div>
    )
  }

  const t = data?.totals

  return (
    <div className='container-fluid py-6' dir='rtl'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <h1 className='fw-bolder m-0'>פרסומים מאושרים</h1>
        <select className='form-select form-select-sm w-auto' value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={1}>היום</option>
          <option value={2}>היום + אתמול</option>
          <option value={3}>3 ימים</option>
          <option value={7}>7 ימים</option>
        </select>
      </div>
      <div className='text-gray-500 fs-7 mb-6'>
        לפי התראות פייסבוק של הפרופילים ("הפוסט שלך שותף בקבוצה", "מנהל אישר את הפוסט שלך", "נדחה").
        פוסט שעולה מיד בקבוצה פתוחה לא מקבל התראה — לכן המספר הוא רף תחתון (ממוצע ~22% מהפרסומים). חשוב לבדוק לקוחות שמקבלים 0.
      </div>

      {t && (
        <div className='row g-4 mb-6'>
          <StatCard label='לקוחות פעילים' value={t.accounts} />
          <StatCard label='לא מפרסמים / לא מאושרים' value={t.red} className={t.red ? 'text-danger' : ''} />
          <StatCard label='אזהרות' value={t.orange} className={t.orange ? 'text-warning' : ''} />
          <StatCard label='הבוט פרסם / אושרו' value={`${t.posted.toLocaleString()} / ${t.live.toLocaleString()}`} />
        </div>
      )}

      <div className='card card-flush mb-4'>
        <div className='card-body py-4 d-flex flex-wrap gap-3 align-items-center'>
          <div className='btn-group'>
            <button className={`btn btn-sm ${tab === 'accounts' ? 'btn-primary' : 'btn-light'}`} onClick={() => setTab('accounts')}>לקוחות</button>
            <button className={`btn btn-sm ${tab === 'groups' ? 'btn-primary' : 'btn-light'}`} onClick={() => setTab('groups')}>קבוצות פעילות</button>
          </div>
          {tab === 'accounts' && (
            <div className='form-check form-switch m-0'>
              <input className='form-check-input' type='checkbox' id='alertsOnly' checked={alertsOnly} onChange={(e) => setAlertsOnly(e.target.checked)} />
              <label className='form-check-label fs-7' htmlFor='alertsOnly'>רק לקוחות עם בעיה</label>
            </div>
          )}
          <input
            className='form-control form-control-sm w-auto'
            style={{minWidth: 240}}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'accounts' ? 'חיפוש לקוח / טלפון' : 'חיפוש קבוצה'}
          />
          <button className='btn btn-sm btn-light ms-auto' onClick={load} disabled={loading}>
            <i className='bi bi-arrow-clockwise'></i> רענון
          </button>
        </div>
      </div>

      <div className='card card-flush'>
        <div className='card-body py-4'>
          {loading ? (
            <div className='text-center py-10'><span className='spinner-border text-primary' role='status'></span></div>
          ) : !data ? (
            <div className='text-center text-gray-500 py-10'>שגיאה בטעינה</div>
          ) : tab === 'accounts' ? (
            accounts.length === 0 ? (
              <div className='text-center text-gray-500 py-10'>{alertsOnly ? 'אין לקוחות עם בעיה 🎉' : 'אין נתונים'}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3 fs-7'>
                  <thead>
                    <tr className='fw-bold text-gray-600'>
                      <th>מצב</th><th>לקוח</th><th>פרופילים</th><th>הגיע זמנם</th><th title="בסוגריים: כמה מהם כבר אפשר לבדוק (הפרופיל סנכרן התראות אחריהם)">הבוט פרסם</th><th>אושרו</th><th>יחס</th><th>שבוע קודם (ליום)</th><th>נדחו</th><th>קבוצות</th><th>אישור אחרון</th><th>סנכרון התראות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((a) => (
                      <tr key={a.account_id} style={{cursor: 'pointer'}} onClick={() => setSelected(a.account_id)}>
                        <td><span className={`badge ${LEVEL_BADGE[a.alert]}`} title={ALERT_HINTS[a.alert_code]}>{ALERT_LABELS[a.alert_code]}</span></td>
                        <td>
                          <div className='fw-bold'>{a.name}</div>
                          <div className='text-gray-500 fs-8' dir='ltr' style={{textAlign: 'right'}}>{a.phone}</div>
                        </td>
                        <td>{a.profiles}</td>
                        <td>{a.due}</td>
                        <td>{a.posted} <span className='text-gray-400'>({a.posted_checked})</span></td>
                        <td className={a.alert === 'red' ? 'text-danger fw-bolder' : 'fw-bolder'}>{a.live}</td>
                        <td>{pct(a.live, a.posted_checked)}</td>
                        <td className='text-gray-600'>{a.live_prev_per_day || '—'}</td>
                        <td className={a.declined ? 'text-danger' : 'text-gray-400'}>{a.declined}</td>
                        <td>{a.groups_live}</td>
                        <td className='text-nowrap'>{ago(a.last_live_at)}</td>
                        <td className='text-gray-500 text-nowrap'>{ago(a.last_notif_sync)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : groups.length === 0 ? (
            <div className='text-center text-gray-500 py-10'>אין נתונים</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3 fs-7'>
                <thead>
                  <tr className='fw-bold text-gray-600'>
                    <th>קבוצה</th><th>פוסטים שאושרו</th><th>נדחו</th><th>לקוחות</th><th>פרופילים</th><th>אחרון</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.group_fb_id}>
                      <td><a href={groupLink(g.group_fb_id)} target='_blank' rel='noreferrer'>{g.name || g.group_fb_id}</a></td>
                      <td className='fw-bolder'>{g.live}</td>
                      <td className={g.declined ? 'text-danger' : 'text-gray-400'}>{g.declined}</td>
                      <td>{g.accounts}</td>
                      <td>{g.profiles}</td>
                      <td className='text-nowrap'>{ago(g.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PostConfirmationsPage
